import React, { useState, useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { Dialog, DialogContent, DialogActions, TextField, CircularProgress } from '@mui/material'
import { InputLabel, Grid } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import { Button as ReferaButton } from '@refera/ui-web'
import { makeStyles } from '@material-ui/styles'

import { Select } from '_/components/inputs'
import * as Input from '_/components/inputs/Input'
import generalInputStyles from '_/components/inputs/styles'
import { patchAssistant, postAiResponse } from '_/modules/ai-integration/actions'
import { aiModelsListSelector } from '_modules/ai-integration/selectors'
import { REASONING_EFFORT_OPTIONS } from '../utils/constants'

const useStyles = makeStyles(({ spacing }) => ({
  dialogPaper: {
    maxWidth: 900,
    width: '100%',
    minHeight: 600,
  },
  dialogTitle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: `${spacing(16)}px ${spacing(24)}px`,
    borderBottom: '1px solid #e0e0e0',
  },
  dialogTitleText: {
    fontSize: spacing(18),
    fontWeight: 500,
  },
  content: {
    padding: `${spacing(24)}px !important`,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing(24),
  },
  label: {
    marginBottom: spacing(4),
  },
  responseArea: {
    minHeight: 200,
    maxHeight: 400,
    overflow: 'auto',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: spacing(16),
    backgroundColor: '#fafafa',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: spacing(14),
    lineHeight: 1.6,
  },
  responseError: {
    color: '#d32f2f',
    border: '1px solid #d32f2f',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 120,
    gap: spacing(12),
    fontSize: spacing(14),
  },
  placeholderText: {
    color: '#999',
    fontSize: spacing(14),
  },
  reasoningArea: {
    minHeight: 100,
    maxHeight: 250,
    overflow: 'auto',
    border: '1px solid #b0bec5',
    borderRadius: 4,
    padding: spacing(16),
    backgroundColor: '#f5f5f5',
    whiteSpace: 'pre-wrap',
    fontFamily: 'monospace',
    fontSize: spacing(14),
    lineHeight: 1.6,
  },
  actions: {
    padding: `${spacing(16)}px ${spacing(24)}px`,
    borderTop: '1px solid #e0e0e0',
  },
}))

export default function AiTestModal({ open, onClose, assistantId, formValues }) {
  const styles = useStyles()
  const generalStyles = generalInputStyles()
  const dispatch = useDispatch()
  const aiModels = useSelector(aiModelsListSelector)

  const methods = useForm({
    mode: 'all',
    defaultValues: {
      model: formValues?.model || '',
      reasoningEffort: formValues?.reasoningEffort || '',
      temperature: formValues?.temperature ?? 0,
    },
  })

  const { getValues, watch } = methods
  const watchReasoning = watch('reasoningEffort')

  const [userInput, setUserInput] = useState('')
  const [response, setResponse] = useState(null)
  const [reasoning, setReasoning] = useState(null)
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState(null)
  const [hasPatched, setHasPatched] = useState(false)

  const slug = formValues?.slug

  const includeReasoning = useMemo(() => {
    return !!watchReasoning && watchReasoning !== ''
  }, [watchReasoning])

  const handleProcess = useCallback(async () => {
    if (!userInput.trim() || !slug) return

    setIsProcessing(true)
    setError(null)
    setResponse(null)
    setReasoning(null)

    try {
      const currentValues = getValues()

      if (assistantId) {
        const tempValue = currentValues.temperature
        const patchPayload = {
          model: currentValues.model,
          reasoningEffort: currentValues.reasoningEffort || null,
          temperature: Number(String(tempValue ?? 0).replace(',', '.') || 0),
        }
        await dispatch(patchAssistant(assistantId, patchPayload))
        setHasPatched(true)
      }

      const result = await dispatch(
        postAiResponse({
          modelSlug: slug,
          input: userInput,
          includeReasoning,
        })
      )

      if (result?.reasoning) {
        setReasoning(result.reasoning)
      }

      const mainResponse = result?.response ?? result
      setResponse(mainResponse)
    } catch (err) {
      setError(
        typeof err === 'object' ? JSON.stringify(err, null, 2) : String(err || 'Erro ao processar')
      )
    } finally {
      setIsProcessing(false)
    }
  }, [userInput, slug, assistantId, includeReasoning, getValues, dispatch])

  const formatContent = content => {
    if (content === null || content === undefined) return ''
    return typeof content === 'object' ? JSON.stringify(content, null, 2) : String(content)
  }

  return (
    <Dialog open={open} onClose={() => onClose(hasPatched)} classes={{ paper: styles.dialogPaper }}>
      <div className={styles.dialogTitle}>
        <span className={styles.dialogTitleText}>Testar IA — {formValues?.name || slug}</span>
        <IconButton onClick={() => onClose(hasPatched)} size="small">
          <CloseIcon />
        </IconButton>
      </div>
      <DialogContent className={styles.content}>
        <FormProvider {...methods}>
          <div>
            <InputLabel
              variant="standard"
              className={`${generalStyles.inputLabelTextArea} ${styles.label}`}
            >
              Input do usuário
            </InputLabel>
            <TextField
              value={userInput}
              onChange={e => setUserInput(e.target.value)}
              multiline
              minRows={3}
              maxRows={8}
              fullWidth
              variant="outlined"
              placeholder="Cole aqui o texto para enviar à IA..."
              InputProps={{ style: { fontSize: '1.4rem', paddingLeft: '8px' } }}
            />
          </div>

          <Grid container spacing={3}>
            <Grid item xs={4}>
              <Select
                label="Modelo"
                name="model"
                options={aiModels || []}
                getLabel={item => item.name}
                getValue={item => item.name}
                getKey={item => item.id || item.name}
                defaultValue={formValues?.model || ''}
              />
            </Grid>
            <Grid item xs={4}>
              <Select
                label="Esforço de raciocínio"
                name="reasoningEffort"
                options={REASONING_EFFORT_OPTIONS}
                getLabel={item => item.label}
                getValue={item => item.value}
                getKey={item => item.value || 'empty'}
                defaultValue={formValues?.reasoningEffort || ''}
                emptyValue="Nenhum (null)"
              />
            </Grid>
            <Grid item xs={4}>
              <Input.Root name="temperature" defaultValue={formValues?.temperature ?? 0}>
                <Input.Label name="temperature">Temperatura</Input.Label>
                <Input.ControllerNumber name="temperature" decimalScale={1} />
              </Input.Root>
            </Grid>
          </Grid>
        </FormProvider>

        {reasoning !== null && (
          <div>
            <InputLabel
              variant="standard"
              className={`${generalStyles.inputLabelTextArea} ${styles.label}`}
            >
              Raciocínio
            </InputLabel>
            <div className={styles.reasoningArea}>{formatContent(reasoning)}</div>
          </div>
        )}

        <div>
          <InputLabel
            variant="standard"
            className={`${generalStyles.inputLabelTextArea} ${styles.label}`}
          >
            Resposta
          </InputLabel>
          {isProcessing ? (
            <div className={styles.loadingContainer}>
              <CircularProgress size={24} />
              <span>Processando...</span>
            </div>
          ) : error ? (
            <div className={`${styles.responseArea} ${styles.responseError}`}>{error}</div>
          ) : response === null ? (
            <div className={styles.responseArea}>
              <span className={styles.placeholderText}>A resposta da IA aparecerá aqui...</span>
            </div>
          ) : (
            <div className={styles.responseArea}>{formatContent(response)}</div>
          )}
        </div>
      </DialogContent>
      <DialogActions className={styles.actions}>
        <ReferaButton variant="secondary" color="red" onClick={() => onClose(hasPatched)}>
          Fechar
        </ReferaButton>
        <ReferaButton
          variant="primary"
          onClick={handleProcess}
          disabled={isProcessing || !userInput.trim() || !slug}
        >
          {isProcessing ? 'Processando...' : 'Salvar e Processar'}
        </ReferaButton>
      </DialogActions>
    </Dialog>
  )
}
